import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  getMobileConfigByCode,
  isValidMobile,
} from '@/features/auth/utils/mobileNumber';
import {
  useProfileQuery,
  useUpdateProfileMutation,
  type ProfileData,
} from '@/features/profile/api/useProfileQueries';
import { useIsClient } from '@/shared/hooks/useIsClient';
import type { ProfileOverviewForm } from '../../ProfilePage.types';

const EMPTY_FORM: ProfileOverviewForm = {
  firstName: '',
  lastName: '',
  email: '',
  countryCode: '',
  mobileNumber: '',
  fullAddress: '',
  locality: '',
  pincode: '',
  city: '',
  state: '',
  country: '',
};

function toFormState(user: ProfileData): ProfileOverviewForm {
  return {
    firstName: user.firstName || user.name?.split(' ')[0] || '',
    lastName: user.lastName || user.name?.split(' ').slice(1).join(' ') || '',
    email: user.email || '',
    countryCode: String(user.mobileCountryCode || '+91'),
    mobileNumber: String(user.mobileNumber || user.phone || '').replace(/\D/g, ''),
    fullAddress: user.address?.fullAddress || '',
    locality: user.address?.locality || '',
    pincode: user.address?.pincode || '',
    city: user.address?.city || '',
    state: user.address?.state || '',
    country: user.address?.country || user.country || '',
  };
}

function toDynamicFormState(user: ProfileData): Record<string, string> {
  const df: Record<string, string> = {};
  if (user.dynamicFields) {
    Object.entries(user.dynamicFields).forEach(([key, val]) => {
      if (val && typeof val === 'object' && (val as Record<string, unknown>).url) return;
      const s = String(val ?? '');
      df[key] = s === 'undefined' || s === 'null' ? '' : s;
    });
  }
  return df;
}

export function scrollToProfileField(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  if (el instanceof HTMLElement) el.focus({ preventScroll: true });
}

function compactRecord(record: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) out[key] = trimmed;
      continue;
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const nested = compactRecord(value as Record<string, unknown>);
      if (Object.keys(nested).length) out[key] = nested;
      continue;
    }
    if (value != null && value !== '') out[key] = value;
  }
  return out;
}

/** Owns the profile-overview edit form: field state, sync from the fetched profile, and save. */
export function useProfileOverviewForm() {
  const profile = useProfileQuery();
  const updateMutation = useUpdateProfileMutation();
  const isClient = useIsClient();
  const user = profile.data?.data;

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<ProfileOverviewForm>(EMPTY_FORM);
  const [dynamicForm, setDynamicForm] = useState<Record<string, string>>({});
  const [savedDynamicForm, setSavedDynamicForm] = useState<Record<string, string>>(
    {},
  );
  const [nameErrors, setNameErrors] = useState<{
    firstName?: string;
    lastName?: string;
  }>({});

  useEffect(() => {
    if (user) {
      setForm(toFormState(user));
      const nextDynamic = toDynamicFormState(user);
      setDynamicForm(nextDynamic);
      setSavedDynamicForm(nextDynamic);
    }
  }, [user]);

  const handleCancel = () => {
    if (user) {
      setForm(toFormState(user));
      setDynamicForm(savedDynamicForm);
    }
    setNameErrors({});
    setEditing(false);
  };

  const clearNameError = (field: 'firstName' | 'lastName') => {
    setNameErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validateRequiredNames = () => {
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const nextNameErrors: { firstName?: string; lastName?: string } = {};

    if (isClient) {
      if (!firstName) {
        nextNameErrors.firstName = 'Key contact person name is required';
      }
      setNameErrors(nextNameErrors);
      if (nextNameErrors.firstName) {
        scrollToProfileField('profile-firstName');
        return false;
      }
      return true;
    }

    if (!firstName) nextNameErrors.firstName = 'First name is required';
    if (!lastName) nextNameErrors.lastName = 'Last name is required';
    setNameErrors(nextNameErrors);
    if (nextNameErrors.firstName) {
      scrollToProfileField('profile-firstName');
      return false;
    }
    if (nextNameErrors.lastName) {
      scrollToProfileField('profile-lastName');
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (!validateRequiredNames()) return;

    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();

    const mobileConfig = getMobileConfigByCode(form.countryCode);
    if (form.mobileNumber && !isValidMobile(form.mobileNumber, mobileConfig)) {
      toast.error(mobileConfig.message);
      return;
    }

    const payload = compactRecord({
      firstName,
      lastName,
      mobileNumber: form.mobileNumber,
      mobileCountryCode: form.mobileNumber ? form.countryCode : '',
      address: {
        fullAddress: form.fullAddress,
        locality: form.locality,
        pincode: form.pincode,
        city: form.city,
        state: form.state,
        country: form.country,
      },
      ...dynamicForm,
    });
    updateMutation.mutate(payload, {
      onSuccess: () => {
        setSavedDynamicForm(dynamicForm);
        setNameErrors({});
        setEditing(false);
      },
    });
  };

  return {
    profile,
    user,
    editing,
    setEditing,
    form,
    setForm,
    dynamicForm,
    setDynamicForm,
    savedDynamicForm,
    nameErrors,
    clearNameError,
    isSaving: updateMutation.isPending,
    validateRequiredNames,
    handleSave,
    handleCancel,
  };
}
